import React, { useState, useEffect, useMemo } from 'react';
import {
    Check,
    Send,
    ShieldAlert,
    Star,
    School,
    FileText,
    Sparkles,
    ChevronDown,
    Users,
    LayoutDashboard,
    List,
    BarChart3,
    UserCircle,
    LogOut,
    TrendingUp,
    AlertTriangle,
    Filter,
    Lock,
    Search,
    Calendar,
    PieChart,
    X as XIcon,
    Mail,
    Clock,
    Mic,

    MicOff,
    Archive,
    RefreshCcw
} from 'lucide-react';

import {
    collection,
    addDoc,
    onSnapshot,
    query,
    orderBy,

    serverTimestamp,
    updateDoc,
    doc
} from 'firebase/firestore';

import REAL_STUDENTS_DATA from './data/studentsData.json';

// --- MOCK DATA GENERATION ---

const PROFESSORES_MOCK = [
    "Prof. Carlos Silva (Matemática)",
    "Profa. Ana Paula (Português)",
    "Prof. Roberto (História)",
    "Profa. Júlia (Ciências)",
    "Prof. Marcos (Ed. Física)"
];

const NARRATIVAS_COMPORTAMENTO = {
    elogios: {
        "Participação ativa": "tem se destacado pela participação ativa durante as aulas, contribuindo com ideias que enriquecem o debate",
        "Tarefa completa": "demonstra compromisso ao entregar todas as tarefas propostas, o que tem auxiliado muito na fixação do conteúdo",
        "Comportamento exemplar": "apresenta uma postura exemplar de respeito e colaboração, servindo de referência positiva para a turma",
        "Ajudou colegas": "demonstrou solidariedade ao auxiliar os colegas nas atividades, fortalecendo o trabalho em equipe",
        "Material completo": "mantém a organização trazendo sempre o material necessário, o que facilita o acompanhamento das aulas",
        "Excelente desempenho": "vem apresentando um desempenho acadêmico acima da média, fruto de sua dedicação constante"
    },
    ocorrencias: {
        "Sem dever de casa": "tem deixado de apresentar as tarefas de casa com frequência, o que compromete a fixação dos exercícios vistos em sala",
        "Conversa paralela": "apresenta momentos constantes de conversa paralela que acabam desviando seu foco e o dos colegas durante as explicações",
        "Desrespeito ao professor": "houve episódios em que a comunicação com a equipe docente não seguiu o padrão de respeito e cordialidade esperado no ambiente escolar",
        "Uso de celular": "o uso do aparelho celular em momentos indevidos tem sido um fator recorrente de distração, dificultando a concentração nas atividades",
        "Atraso injustificado": "as chegadas tardias têm prejudicado o acompanhamento do início das aulas e a organização da rotina escolar",
        "Sem material": "a falta frequente do material didático tem limitado sua capacidade de acompanhar plenamente as atividades propostas"
    }
};

const COMPORTAMENTOS_LISTA = {
    elogios: Object.keys(NARRATIVAS_COMPORTAMENTO.elogios),
    ocorrencias: Object.keys(NARRATIVAS_COMPORTAMENTO.ocorrencias)
};

// Gerador de alunos fictícios COM E-MAILS
const gerarAlunos = (turma) => {
    const nomes = ["Ana", "Bruno", "Carlos", "Daniela", "Eduardo", "Fernanda", "Gabriel", "Helena", "Igor", "Julia", "Lucas", "Mariana", "Nicolas", "Olivia", "Pedro", "Stephanie", "Yasmin", "Enzo"];
    const sobrenomes = ["Silva", "Santos", "Oliveira", "Souza", "Rodrigues", "Ferreira", "Almeida", "Pereira", "Lima", "Gomes"];

    return Array.from({ length: 12 }).map((_, i) => {
        const nome = nomes[i % nomes.length];
        const sobrenome = sobrenomes[i % sobrenomes.length];
        return {
            id: `${turma}-${i}`,
            nome: `${nome} ${sobrenome}`,
            // Aqui simulamos a base de dados com o e-mail dos pais
            emailResponsavel: `pais.${nome.toLowerCase()}.${sobrenome.toLowerCase()}@exemplo.com`,
            foto: `https://api.dicebear.com/7.x/avataaars/svg?seed=${turma}-${i}&backgroundColor=b6e3f4`,
            turma: turma
        };
    });
};

// --- LÓGICA DE IA & SANITIZAÇÃO ---

const getArtigo = (nome) => {
    if (!nome) return "o";
    const primeiroNome = nome.split(" ")[0].toLowerCase();
    if (primeiroNome.endsWith("a") || primeiroNome === "yasmin" || primeiroNome === "liz" || primeiroNome === "stephanie") return "a";
    return "o";
};

const sanitizarDescricao = (texto) => {
    if (!texto) return "";
    const textoLower = texto.toLowerCase();

    const termosGraves = [
        "merda", "merdinha", "bosta", "bostinha",
        "puta", "puto", "putinha", "filho da puta",
        "caralho", "cu", "cuzão", "cuzinho",
        "viado", "viadinho", "corno", "arrombado", "fudido", "foder",
        "imbecil", "retardado", "idiota", "burro", "estúpido",
        "babaca", "vagabundo", "inútil", "trouxa", "otário", "otaria",
        "desgraçado", "miserável", "animal", "jumento", "besta",
        "comunista", "fascista", "nazista"
    ];

    if (termosGraves.some(termo => textoLower.includes(termo))) {
        return "Nota-se também o uso de linguagem ou postura gravemente inadequada e desrespeitosa direcionada à equipe escolar ou aos colegas, o que fere as normas de convivência";
    }

    let novoTexto = texto;
    const substituicoesLeves = {
        "bagunça": "agitação", "preguiça": "pouca disposição", "conversinha": "dispersão",
        "brincadeira": "comportamento imaturo", "mentira": "inverdade", "mentiu": "faltou com a verdade",
        "boca suja": "vocabulário inadequado", "palavrão": "termos impróprios", "palavrões": "termos impróprios",
        "chato": "insistente", "respondão": "resistência às orientações"
    };

    Object.keys(substituicoesLeves).forEach(termo => {
        const regex = new RegExp(`\\b${termo}\\b`, "gi");
        novoTexto = novoTexto.replace(regex, substituicoesLeves[termo]);
    });

    return novoTexto;
};

const gerarRelatorioIA = (dados) => {
    const { alunos, ocorrencias, elogios, outros, descricao } = dados;
    const isPlural = alunos.length > 1;
    const nomesFormatados = alunos.map(a => a.nome.split(" ")[0]).join(isPlural ? ", " : "");

    let artigo = "o";
    let pronome = "ele";
    let sujeitoDefinido = "o aluno";

    if (!isPlural) {
        const genero = getArtigo(alunos[0].nome);
        artigo = genero;
        pronome = genero === "a" ? "ela" : "ele";
        sujeitoDefinido = genero === "a" ? "a aluna" : "o aluno";
    } else {
        artigo = "os";
        pronome = "eles";
        sujeitoDefinido = "os alunos";
    }

    const descricaoSanitizada = sanitizarDescricao(descricao);
    const outrosSanitizado = sanitizarDescricao(outros);
    const temElogios = elogios.length > 0;
    const temOcorrencias = ocorrencias.length > 0;

    let texto = `Prezada família,\n\nEsperamos que estejam bem. Entramos em contato para conversar sobre o processo de aprendizagem e a convivência escolar d${artigo} ${nomesFormatados}.\n\n`;

    if (temOcorrencias) {
        texto += `Gostaríamos de chamar a atenção para alguns pontos importantes observados recentemente em sala de aula. `;
        const frases = ocorrencias.map(tag => NARRATIVAS_COMPORTAMENTO.ocorrencias[tag]);
        if (frases.length === 1) texto += `Notamos que ${sujeitoDefinido} ${frases[0]}. `;
        else {
            texto += `Inicialmente, notamos que ${sujeitoDefinido} ${frases[0]}. `;
            for (let i = 1; i < frases.length; i++) {
                const conectivo = i === frases.length - 1 ? "Por fim, " : "Além disso, ";
                texto += `${conectivo}observa-se que ${pronome} também ${frases[i]}. `;
            }
        }
        if (outrosSanitizado) texto += `Somado a isso, ${outrosSanitizado}. `;
        texto += `Esses comportamentos acabam impactando diretamente a qualidade do aprendizado.\n\n`;
    }

    if (temElogios) {
        texto += temOcorrencias ? `Por outro lado, é fundamental valorizar os acertos. ` : `Temos a satisfação de compartilhar ótimas notícias! `;
        const frases = elogios.map(tag => NARRATIVAS_COMPORTAMENTO.elogios[tag]);
        if (frases.length === 1) texto += `Percebemos que ${sujeitoDefinido} ${frases[0]}. `;
        else {
            texto += `${sujeitoDefinido} ${frases[0]}. `;
            for (let i = 1; i < frases.length; i++) texto += `Também notamos que ${pronome} ${frases[i]}. `;
        }
        texto += `Essas atitudes demonstram amadurecimento e comprometimento com os estudos.\n\n`;
    }

    if (descricaoSanitizada) {
        // INTEGRAÇÃO FLUIDA:
        // Removemos o "Vale ressaltar" e inserimos o texto diretamente, ajustando a maiúscula.
        const isOfensaGrave = descricaoSanitizada.startsWith("Nota-se também o uso de linguagem");
        if (isOfensaGrave) {
            texto += `${descricaoSanitizada}.\n\n`;
        } else {
            // Garante que começa com maiúscula para parecer uma nova frase do parágrafo
            const descCapitalized = descricaoSanitizada.charAt(0).toUpperCase() + descricaoSanitizada.slice(1);
            // Adiciona ponto final se não tiver
            const descFinal = descCapitalized.endsWith('.') ? descCapitalized : `${descCapitalized}.`;

            // Usa um conectivo muito suave ou apenas o texto
            texto += `Sobre isso: ${descFinal}\n\n`;
        }
    }

    if (temOcorrencias) texto += `Nosso objetivo é puramente educativo. Acreditamos no potencial d${artigo} ${nomesFormatados} e contamos com o apoio da família.\n\n`;
    else texto += `Ficamos felizes com esse desenvolvimento. Parabéns pela parceria!\n\n`;

    texto += `Cordialmente,\nEquipe Pedagógica`;
    return texto;
};

// --- HELPERS ---

// Função de cópia robusta para iframes/ambientes restritos
const copyToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    try {
        document.execCommand('copy');
        alert('Texto copiado para a área de transferência!');
    } catch (err) {
        console.error('Falha ao copiar', err);
        alert('Erro ao copiar texto. Tente selecionar e copiar manualmente.');
    }
    document.body.removeChild(textArea);
};

// --- COMPONENTES AUXILIARES ---

const StatCard = ({ title, value, color, icon: Icon, subtitle }) => (
    <div className={`bg-white p-5 rounded-2xl shadow-sm border border-slate-100 flex items-center justify-between`}>
        <div>
            <p className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-1">{title}</p>
            <h3 className={`text-3xl font-bold ${color}`}>{value}</h3>
            {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-xl bg-opacity-10 ${color.replace('text-', 'bg-')}`}>
            <Icon className={color} size={24} />
        </div>
    </div>
);

// --- COMPONENTE DE ESTATÍSTICAS DO ALUNO E HISTÓRICO ---
const StudentProfileStats = ({ reports, student, studentClass, trimestres }) => {
    const stats = useMemo(() => {
        if (!student) return null;

        // Filtra relatórios deste aluno específico
        const studentReports = reports.filter(r => r.alunos.some(a => a.nome === student.nome));

        let totalO = 0;
        let totalE = 0;
        let byTrimestre = {};
        let aggregatedOcorrencias = {};
        let aggregatedElogios = {};

        studentReports.forEach(r => {
            const ocCount = r.ocorrencias ? r.ocorrencias.length : 0;
            const elCount = r.elogios ? r.elogios.length : 0;

            totalO += ocCount;
            totalE += elCount;

            const tri = r.trimestre || "Sem Trimestre";
            if (!byTrimestre[tri]) byTrimestre[tri] = { o: 0, e: 0 };
            byTrimestre[tri].o += ocCount;
            byTrimestre[tri].e += elCount;

            r.ocorrencias?.forEach(t => aggregatedOcorrencias[t] = (aggregatedOcorrencias[t] || 0) + 1);
            r.elogios?.forEach(t => aggregatedElogios[t] = (aggregatedElogios[t] || 0) + 1);
        });

        // Convert to arrays for rendering
        const listOcorrencias = Object.entries(aggregatedOcorrencias).sort(([, a], [, b]) => b - a);
        const listElogios = Object.entries(aggregatedElogios).sort(([, a], [, b]) => b - a);

        // Sorting reports
        const sortedReports = studentReports.sort((a, b) => b.timestamp?.seconds - a.timestamp?.seconds);

        return { totalO, totalE, byTrimestre, listOcorrencias, listElogios, reports: sortedReports };
    }, [reports, student]);

    if (!stats) return <div className="p-4 text-center text-slate-500">Dados indisponíveis.</div>;

    return (
        <div className="space-y-6">
            {/* HEADER DO PERFIL */}
            <div className="flex items-center gap-4 mb-2 bg-orange-50 p-4 rounded-2xl border border-orange-100">
                <img src={student.foto} alt={student.nome} className="w-16 h-16 rounded-full bg-white border-2 border-orange-200" />
                <div>
                    <h2 className="text-xl font-bold text-slate-800">{student.nome}</h2>
                    <p className="text-sm text-orange-700 font-medium">{student.turma}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><Mail size={12} /> {student.emailResponsavel}</p>
                </div>
            </div>

            {/* STATS RÁPIDOS */}
            <div className="grid grid-cols-2 gap-4">
                <StatCard title="Total Ocorrências" value={stats.totalO} color="text-red-600" icon={AlertTriangle} />
                <StatCard title="Total Elogios" value={stats.totalE} color="text-emerald-600" icon={Star} />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white p-5 rounded-2xl border border-slate-200">
                    <h4 className="font-bold text-slate-700 mb-4 text-sm">Evolução por Trimestre</h4>
                    <div className="space-y-3">
                        {trimestres.map(tri => {
                            const data = stats.byTrimestre[tri] || { o: 0, e: 0 };
                            const total = data.o + data.e;
                            if (total === 0) return (
                                <div key={tri} className="flex justify-between text-sm opacity-50">
                                    <span>{tri}</span>
                                    <span>-</span>
                                </div>
                            );
                            return (
                                <div key={tri} className="space-y-1">
                                    <div className="flex justify-between text-sm font-medium">
                                        <span>{tri}</span>
                                        <span className="text-xs text-slate-500">{data.o} oc. / {data.e} el.</span>
                                    </div>
                                    <div className="h-2 bg-slate-100 rounded-full flex overflow-hidden">
                                        <div style={{ width: `${(data.o / total) * 100}%` }} className="bg-red-400"></div>
                                        <div style={{ width: `${(data.e / total) * 100}%` }} className="bg-emerald-400"></div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>

                <div className="bg-white p-5 rounded-2xl border border-slate-200">
                    <h4 className="font-bold text-slate-700 mb-4 text-sm">Detalhamento de Comportamentos</h4>
                    <div className="space-y-4">
                        <div>
                            <p className="text-xs font-bold text-red-500 uppercase mb-2 border-b border-red-100 pb-1">Ocorrências</p>
                            <ul className="space-y-1">
                                {stats.listOcorrencias.map(([tag, count]) => (
                                    <li key={tag} className="flex justify-between text-xs p-1.5 bg-red-50 rounded text-red-800">
                                        <span>{tag}</span>
                                        <span className="font-bold">{count}x</span>
                                    </li>
                                ))}
                                {stats.listOcorrencias.length === 0 && <p className="text-xs text-slate-400 italic">Nenhuma ocorrência.</p>}
                            </ul>
                        </div>
                        <div>
                            <p className="text-xs font-bold text-emerald-500 uppercase mb-2 border-b border-emerald-100 pb-1">Elogios</p>
                            <ul className="space-y-1">
                                {stats.listElogios.map(([tag, count]) => (
                                    <li key={tag} className="flex justify-between text-xs p-1.5 bg-emerald-50 rounded text-emerald-800">
                                        <span>{tag}</span>
                                        <span className="font-bold">{count}x</span>
                                    </li>
                                ))}
                                {stats.listElogios.length === 0 && <p className="text-xs text-slate-400 italic">Nenhum elogio.</p>}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* HISTÓRICO DETALHADO */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200">
                <h4 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <List size={20} className="text-orange-700" /> Histórico de Registros
                </h4>

                <div className="space-y-4 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                    {stats.reports.length === 0 ? (
                        <p className="text-center text-slate-400 text-sm py-4">Nenhum registro encontrado para este aluno.</p>
                    ) : (
                        stats.reports.map(report => (
                            <div key={report.id} className="border-l-4 border-slate-200 pl-4 py-1 hover:border-orange-400 transition-colors">
                                <div className="flex justify-between items-start mb-1">
                                    <div>
                                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">{report.data} • {report.trimestre}</span>
                                        <p className="text-sm font-bold text-slate-800">{report.professor}</p>
                                    </div>
                                    <div className="flex gap-1">
                                        {report.ocorrencias?.length > 0 && <span className="w-2 h-2 rounded-full bg-red-500" title="Ocorrência"></span>}
                                        {report.elogios?.length > 0 && <span className="w-2 h-2 rounded-full bg-emerald-500" title="Elogio"></span>}
                                    </div>
                                </div>

                                <div className="text-sm text-slate-600 mb-2">
                                    {[...(report.ocorrencias || []), ...(report.elogios || [])].join(", ")}
                                </div>

                                {report.descricao && (
                                    <div className="text-xs bg-slate-50 p-2 rounded text-slate-500 italic">
                                        "{report.descricao}"
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};


// --- DASHBOARDS ---

const TeacherDashboard = ({ reports, professorName, dbAlunos, turmas, trimestres }) => {
    const [activeTab, setActiveTab] = useState('profile');
    const [selectedClass, setSelectedClass] = useState("");
    const [selectedStudentName, setSelectedStudentName] = useState("");

    const myReports = useMemo(() => reports.filter(r => r.professor === professorName), [reports, professorName]);

    const availableStudents = useMemo(() => {
        if (selectedClass) return dbAlunos[selectedClass];
        return [];
    }, [selectedClass, dbAlunos]);

    // Handler para quando clicar em um aluno na lista de logs
    const handleStudentClick = (studentName, className) => {
        setSelectedClass(className);
        setSelectedStudentName(studentName);
        setActiveTab('profile');
    };

    const currentStudentObj = availableStudents.find(s => s.nome === selectedStudentName);

    return (
        <div className="animate-in fade-in space-y-6">
            <div className="flex bg-slate-100 p-1 rounded-lg w-fit">
                <button
                    onClick={() => setActiveTab('profile')}
                    className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${activeTab === 'profile' ? 'bg-white text-orange-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                    Consultar Aluno
                </button>
                <button
                    onClick={() => setActiveTab('my_logs')}
                    className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${activeTab === 'my_logs' ? 'bg-white text-orange-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                    Meus Registros ({myReports.length})
                </button>
            </div>

            {activeTab === 'profile' && (
                <div className="space-y-6">
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                        <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><Search size={20} className="text-orange-700" /> Buscar Perfil do Aluno</h3>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">1. Turma</label>
                                <select
                                    value={selectedClass}
                                    onChange={(e) => { setSelectedClass(e.target.value); setSelectedStudentName(""); }}
                                    className="w-full mt-1 p-2 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-orange-500"
                                >
                                    <option value="">Selecione...</option>
                                    {turmas.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">2. Aluno</label>
                                <select
                                    value={selectedStudentName}
                                    onChange={(e) => setSelectedStudentName(e.target.value)}
                                    disabled={!selectedClass}
                                    className="w-full mt-1 p-2 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-orange-500 disabled:opacity-50"
                                >
                                    <option value="">Selecione...</option>
                                    {availableStudents.map(s => <option key={s.id} value={s.nome}>{s.nome}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>

                    {currentStudentObj && (
                        <div className="animate-in slide-in-from-bottom-4">
                            <StudentProfileStats
                                reports={reports}
                                student={currentStudentObj}
                                studentName={currentStudentObj.nome}
                                studentClass={currentStudentObj.turma}
                                trimestres={trimestres}
                            />
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'my_logs' && (
                <div className="space-y-4">
                    {myReports.length === 0 ? (
                        <div className="text-center py-10 text-slate-400">Você ainda não fez registros.</div>
                    ) : (
                        myReports.map(report => (
                            <div key={report.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex justify-between items-center">
                                <div>
                                    <p className="font-bold text-slate-700 text-sm">{report.turma} • {report.trimestre}</p>
                                    <p className="text-xs text-slate-500">{report.data}</p>
                                    <p className="text-sm mt-1">
                                        {/* Tornando alunos clicáveis */}
                                        {report.alunos.map((a, idx) => (
                                            <span key={a.id}>
                                                <span
                                                    onClick={() => handleStudentClick(a.nome, report.turma)}
                                                    className="text-orange-700 hover:text-orange-900 hover:underline cursor-pointer font-medium"
                                                    title="Ver perfil do aluno"
                                                >
                                                    {a.nome}
                                                </span>
                                                {idx < report.alunos.length - 1 ? ", " : ""}
                                            </span>
                                        ))}
                                    </p>
                                </div>
                                <div className="text-right">
                                    {report.ocorrencias?.length > 0 && <span className="block text-xs font-bold text-red-600">{report.ocorrencias.length} Ocorrências</span>}
                                    {report.elogios?.length > 0 && <span className="block text-xs font-bold text-emerald-600">{report.elogios.length} Elogios</span>}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

const AdminDashboard = ({ reports, dbAlunos, allStudentsFlat, turmas, trimestres }) => {
    const [filterTurma, setFilterTurma] = useState("");
    const [filterProfessor, setFilterProfessor] = useState("");
    const [filterAluno, setFilterAluno] = useState("");
    const [filterTrimestre, setFilterTrimestre] = useState("");
    const [studentForModal, setStudentForModal] = useState(null);

    const filteredReports = useMemo(() => {
        return reports.filter(r => {
            const matchTurma = filterTurma ? r.turma === filterTurma : true;
            const matchProf = filterProfessor ? r.professor === filterProfessor : true;
            const matchAluno = filterAluno ? r.alunos.some(a => a.nome === filterAluno) : true;
            const matchTri = filterTrimestre ? r.trimestre === filterTrimestre : true;
            return matchTurma && matchProf && matchAluno && matchTri;
        });
    }, [reports, filterTurma, filterProfessor, filterAluno, filterTrimestre]);

    const stats = useMemo(() => {
        let totalOcorrencias = 0;
        let totalElogios = 0;
        let byTurma = {};
        let aggregatedOcorrencias = {};
        let aggregatedElogios = {};

        filteredReports.forEach(r => {
            totalOcorrencias += r.ocorrencias ? r.ocorrencias.length : 0;
            totalElogios += r.elogios ? r.elogios.length : 0;

            if (!byTurma[r.turma]) byTurma[r.turma] = { o: 0, e: 0 };
            byTurma[r.turma].o += r.ocorrencias ? r.ocorrencias.length : 0;
            byTurma[r.turma].e += r.elogios ? r.elogios.length : 0;

            r.ocorrencias?.forEach(t => aggregatedOcorrencias[t] = (aggregatedOcorrencias[t] || 0) + 1);
            r.elogios?.forEach(t => aggregatedElogios[t] = (aggregatedElogios[t] || 0) + 1);
        });

        const topTurmas = Object.entries(byTurma)
            .sort(([, a], [, b]) => (b.o + b.e) - (a.o + a.e))
            .slice(0, 5);

        const listOcorrencias = Object.entries(aggregatedOcorrencias).sort(([, a], [, b]) => b - a);
        const listElogios = Object.entries(aggregatedElogios).sort(([, a], [, b]) => b - a);

        return { totalReports: filteredReports.length, totalOcorrencias, totalElogios, topTurmas, listOcorrencias, listElogios };
    }, [filteredReports]);

    // CORREÇÃO: Lista de nomes de alunos (únicos) para o filtro
    const availableStudentsNames = useMemo(() => {
        let names = [];
        if (filterTurma) {
            names = dbAlunos[filterTurma].map(a => a.nome);
        } else {
            names = allStudentsFlat.map(a => a.nome);
        }
        return [...new Set(names)].sort();
    }, [filterTurma, dbAlunos, allStudentsFlat]);

    // Lista de OBJETOS de alunos da turma selecionada (para o grid visual)
    const classStudents = useMemo(() => {
        if (filterTurma) return dbAlunos[filterTurma];
        return [];
    }, [filterTurma, dbAlunos]);

    // Derived list of professors who have made reports
    const activeProfessors = useMemo(() => {
        const professors = reports.map(r => r.professor).filter(Boolean);
        return [...new Set(professors)].sort();
    }, [reports]);

    const handleSendEmail = (report) => {
        const aluno = report.alunos[0];
        const email = aluno.emailResponsavel;
        const subject = `Comunicado Escolar - ${aluno.nome}`;
        const body = encodeURIComponent(report.textoGerado);
        window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    };

    return (
        <div className="space-y-6 animate-in fade-in">

            {/* BARRA DE FILTROS */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col gap-4">
                <div className="flex items-center gap-2 text-slate-500 font-bold text-sm">
                    <Filter size={18} /> Filtros:
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 w-full">
                    <div className="relative">
                        <select
                            value={filterTrimestre}
                            onChange={(e) => setFilterTrimestre(e.target.value)}
                            className="w-full pl-3 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                        >
                            <option value="">Todos os Trimestres</option>
                            {trimestres.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <ChevronDown className="absolute right-3 top-2.5 text-slate-400 pointer-events-none" size={16} />
                    </div>

                    <div className="relative">
                        <select
                            value={filterTurma}
                            onChange={(e) => { setFilterTurma(e.target.value); setFilterAluno(""); }}
                            className="w-full pl-3 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                        >
                            <option value="">Todas as Turmas</option>
                            {turmas.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <ChevronDown className="absolute right-3 top-2.5 text-slate-400 pointer-events-none" size={16} />
                    </div>

                    <div className="relative">
                        <select
                            value={filterProfessor}
                            onChange={(e) => setFilterProfessor(e.target.value)}
                            className="w-full pl-3 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                        >
                            <option value="">Todos os Professores</option>
                            {activeProfessors.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                        <ChevronDown className="absolute right-3 top-2.5 text-slate-400 pointer-events-none" size={16} />
                    </div>

                    <div className="relative">
                        <select
                            value={filterAluno}
                            onChange={(e) => setFilterAluno(e.target.value)}
                            className="w-full pl-3 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                        >
                            <option value="">Todos os Alunos {filterTurma ? `do ${filterTurma}` : ''}</option>
                            {availableStudentsNames.map(nome => <option key={nome} value={nome}>{nome}</option>)}
                        </select>
                        <ChevronDown className="absolute right-3 top-2.5 text-slate-400 pointer-events-none" size={16} />
                    </div>
                </div>

                {(filterTurma || filterProfessor || filterAluno || filterTrimestre) && (
                    <button
                        onClick={() => { setFilterTurma(""); setFilterProfessor(""); setFilterAluno(""); setFilterTrimestre(""); }}
                        className="text-xs font-bold text-red-500 hover:text-red-700 w-fit"
                    >
                        Limpar Filtros
                    </button>
                )}
            </div>

            {/* CARDS GERAIS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard title="Registros Filtrados" value={stats.totalReports} color="text-orange-700" icon={FileText} />
                <StatCard title="Ocorrências" value={stats.totalOcorrencias} color="text-red-600" icon={AlertTriangle} />
                <StatCard title="Elogios" value={stats.totalElogios} color="text-emerald-600" icon={Star} />
            </div>

            {/* --- GRID DE ALUNOS DA TURMA (NOVA FEATURE SOLICITADA) --- */}
            {filterTurma && !filterAluno && (
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-orange-100 animate-in slide-in-from-bottom-4">
                    <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                        <Users size={20} className="text-orange-700" />
                        Alunos do {filterTurma}
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        {classStudents.map(student => {
                            // Calcular stats rápidos para o card
                            const studentReports = reports.filter(r => r.alunos.some(a => a.nome === student.nome));
                            const oc = studentReports.reduce((acc, r) => acc + (r.ocorrencias?.length || 0), 0);
                            const el = studentReports.reduce((acc, r) => acc + (r.elogios?.length || 0), 0);

                            return (
                                <div
                                    key={student.id}
                                    onClick={() => setStudentForModal(student)}
                                    className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex flex-col items-center text-center cursor-pointer hover:border-orange-400 hover:shadow-md transition-all group"
                                >
                                    <img src={student.foto} alt={student.nome} className="w-16 h-16 rounded-full bg-white mb-2" />
                                    <h4 className="text-xs font-bold text-slate-700 leading-tight group-hover:text-orange-700">{student.nome}</h4>
                                    <div className="flex gap-2 mt-2 text-[10px] font-bold">
                                        <span className="text-red-500">{oc} Oc.</span>
                                        <span className="text-emerald-600">{el} El.</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* GRAFICOS */}
            <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                    <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                        <BarChart3 size={20} className="text-orange-600" />
                        Top Turmas
                    </h3>
                    <div className="space-y-4">
                        {stats.topTurmas.map(([turma, data]) => (
                            <div key={turma}>
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="font-medium text-slate-700">{turma}</span>
                                    <span className="text-slate-500">{data.o + data.e}</span>
                                </div>
                                <div className="h-2 bg-slate-100 rounded-full overflow-hidden flex">
                                    <div style={{ width: `${(data.o / (data.o + data.e)) * 100}%` }} className="bg-red-400 h-full" />
                                    <div style={{ width: `${(data.e / (data.o + data.e)) * 100}%` }} className="bg-emerald-400 h-full" />
                                </div>
                            </div>
                        ))}
                        {stats.topTurmas.length === 0 && <p className="text-slate-400 text-sm">Sem dados.</p>}
                    </div>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                    <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                        <TrendingUp size={20} className="text-slate-600" />
                        Detalhamento de Comportamentos
                    </h3>
                    <div className="space-y-6">
                        <div>
                            <p className="text-xs font-bold text-red-500 uppercase mb-2 border-b border-red-100 pb-1">Ocorrências</p>
                            <ul className="space-y-1">
                                {stats.listOcorrencias.map(([tag, count]) => (
                                    <li key={tag} className="flex justify-between text-xs p-1.5 bg-red-50 rounded text-red-800">
                                        <span>{tag}</span>
                                        <span className="font-bold">{count}x</span>
                                    </li>
                                ))}
                                {stats.listOcorrencias.length === 0 && <p className="text-xs text-slate-400 italic">Nenhuma ocorrência.</p>}
                            </ul>
                        </div>
                        <div>
                            <p className="text-xs font-bold text-emerald-500 uppercase mb-2 border-b border-emerald-100 pb-1">Elogios</p>
                            <ul className="space-y-1">
                                {stats.listElogios.map(([tag, count]) => (
                                    <li key={tag} className="flex justify-between text-xs p-1.5 bg-emerald-50 rounded text-emerald-800">
                                        <span>{tag}</span>
                                        <span className="font-bold">{count}x</span>
                                    </li>
                                ))}
                                {stats.listElogios.length === 0 && <p className="text-xs text-slate-400 italic">Nenhum elogio.</p>}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* MODAL DE PERFIL DO ALUNO (ADMIN) */}
            {studentForModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in">
                    <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50 sticky top-0 z-10">
                            <h3 className="font-bold text-slate-800">Perfil Detalhado</h3>
                            <button onClick={() => setStudentForModal(null)} className="p-1 hover:bg-slate-200 rounded-full transition-colors">
                                <XIcon size={20} className="text-slate-500" />
                            </button>
                        </div>
                        <div className="p-6">
                            <StudentProfileStats
                                reports={reports}
                                student={studentForModal}
                                studentName={studentForModal.nome}
                                studentClass={studentForModal.turma}
                                trimestres={trimestres}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default function StudentOccurrences({ db, currentUser, appId, classes = [], trimestres = [] }) {
    const [currentView, setCurrentView] = useState('register'); // 'register' | 'my_dashboard' (Para professor)
    const [adminTab, setAdminTab] = useState('feed');
    const [showArchived, setShowArchived] = useState(false);
    const [archivedSearchTerm, setArchivedSearchTerm] = useState('');
    const [archivedFilterClass, setArchivedFilterClass] = useState('');
    const [reports, setReports] = useState([]);

    const isAdmin = currentUser.role === 'admin' || currentUser.role === 'master';

    // Estados do Formulário Professor
    const [selectedTrimestre, setSelectedTrimestre] = useState("");
    const [selectedClass, setSelectedClass] = useState("");
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [selectedOcorrencias, setSelectedOcorrencias] = useState([]);
    const [selectedElogios, setSelectedElogios] = useState([]);
    const [otherReason, setOtherReason] = useState("");
    const [description, setDescription] = useState("");
    const [isRecording, setIsRecording] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [studentForModal, setStudentForModal] = useState(null);

    // --- DERIVED STATE FROM PROPS ---
    const dbAlunos = useMemo(() => {
        const db = {};
        classes.forEach(t => {
            if (REAL_STUDENTS_DATA[t]) {
                db[t] = REAL_STUDENTS_DATA[t];
            } else {
                db[t] = gerarAlunos(t);
            }
        });
        return db;
    }, [classes]);

    const allStudentsFlat = useMemo(() => {
        return Object.values(dbAlunos).flat();
    }, [dbAlunos]);

    // --- FIREBASE DATA SYNC ---
    useEffect(() => {
        if (!currentUser || !db) return;
        const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'reports'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const loadedReports = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            loadedReports.sort((a, b) => b.timestamp?.seconds - a.timestamp?.seconds);
            setReports(loadedReports);
        }, (error) => console.error("Erro ao carregar relatórios:", error));
        return () => unsubscribe();
    }, [currentUser, db, appId]);

    // Handlers
    const handleSendEmail = (report) => {
        const aluno = report.alunos[0];
        const email = aluno.emailResponsavel;
        const subject = `Comunicado Escolar - ${aluno.nome}`;
        const body = encodeURIComponent(report.textoGerado);
        window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    };

    const handleSubmit = async () => {
        if (!selectedTrimestre) return alert("Selecione o Trimestre.");
        if (selectedStudents.length === 0) return alert("Selecione pelo menos um aluno.");
        if (selectedOcorrencias.length === 0 && selectedElogios.length === 0 && !otherReason) return alert("Selecione um motivo.");

        setIsSubmitting(true);

        // Recupera os objetos completos dos alunos selecionados
        const alunosSelecionados = dbAlunos[selectedClass].filter(a => selectedStudents.includes(a.id));

        try {
            // Cria um array de promessas para salvar um relatório INDIVIDUAL para cada aluno
            const promises = alunosSelecionados.map(aluno => {
                // Gera o texto personalizado apenas para ESTE aluno (singular)
                const textoGerado = gerarRelatorioIA({
                    alunos: [aluno], // Array com apenas 1 aluno
                    ocorrencias: selectedOcorrencias,
                    elogios: selectedElogios,
                    outros: otherReason,
                    descricao: description
                });

                const novoRelatorio = {
                    professor: currentUser.name,
                    trimestre: selectedTrimestre,
                    turma: selectedClass,
                    alunos: [aluno], // Salva apenas este aluno no registro
                    ocorrencias: selectedOcorrencias,
                    elogios: selectedElogios,
                    outros: otherReason,
                    descricao: description,
                    textoGerado: textoGerado,
                    data: new Date().toLocaleDateString('pt-BR'),
                    hora: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
                    timestamp: serverTimestamp()
                };

                return addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'reports'), novoRelatorio);
            });

            // Aguarda todos os salvamentos
            await Promise.all(promises);

            // Reset do formulário
            setSelectedStudents([]);
            setSelectedOcorrencias([]);
            setSelectedElogios([]);
            setOtherReason("");
            setDescription("");
            setIsSubmitting(false);
            alert(`Sucesso! ${alunosSelecionados.length} registros individuais foram salvos.`);
        } catch (e) {
            console.error(e);
            alert("Erro ao salvar.");
            setIsSubmitting(false);
        }
    };

    const toggleRecording = () => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            alert("Seu navegador não suporta reconhecimento de voz.");
            return;
        }

        if (isRecording) {
            setIsRecording(false);
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.lang = 'pt-BR';
        recognition.continuous = true;
        recognition.interimResults = true;

        recognition.onstart = () => {
            setIsRecording(true);
        };

        recognition.onresult = (event) => {
            let transcript = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
                transcript += event.results[i][0].transcript;
            }
            // Append logic could be smarter, but simplistic for now:
            // Actually, for continuous: we might just want to append the NEW part.
            // But simple onresult usually gives everything or chunks.
            // Let's just update based on the last result to avoid duplication or use simple replacement if simpler.
            // A safer approach for interim:
            // Just take the definitive final result.

            // SIMPLIFIED APPROACH:
            // Just let user speak one sentence at a time or manage state carefully.
            // Let's implement a "Append" logic:
            const lastResult = event.results[event.results.length - 1];
            if (lastResult.isFinal) {
                setDescription(prev => prev + (prev ? " " : "") + lastResult[0].transcript);
            }
        };

        recognition.onerror = (event) => {
            console.error(event.error);
            setIsRecording(false);
        };

        recognition.onend = () => {
            setIsRecording(false);
        };

        recognition.start();
    };

    const handleStudentToggle = (id) => {
        setSelectedStudents(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
    };

    const handleTagToggle = (tag, type) => {
        if (type === 'elogio') {
            setSelectedElogios(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
        } else {
            setSelectedOcorrencias(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
        }
    };

    const filteredStudents = (selectedClass && dbAlunos[selectedClass]) ? dbAlunos[selectedClass] : [];

    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-800 pb-20">
            <main className="max-w-6xl mx-auto px-4 py-6">

                {/* --- VIEW: PROFESSOR --- */}
                {!isAdmin && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">

                        <div className="flex flex-col md:flex-row gap-4 justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-slate-200">
                            <div className="flex items-center gap-3 text-orange-900">
                                <UserCircle size={32} />
                                <div>
                                    <p className="text-xs font-bold uppercase opacity-70">Professor Logado</p>
                                    <p className="font-bold text-lg leading-none">{currentUser.name}</p>
                                </div>
                            </div>

                            {/* Navegação Professor */}
                            <div className="flex bg-slate-100 p-1 rounded-lg">
                                <button
                                    onClick={() => setCurrentView('register')}
                                    className={`flex items-center gap-2 px-6 py-2 rounded-md text-sm font-bold transition-all ${currentView === 'register' ? 'bg-orange-600 text-white shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <Send size={16} /> Registrar
                                </button>
                                <button
                                    onClick={() => setCurrentView('my_dashboard')}
                                    className={`flex items-center gap-2 px-6 py-2 rounded-md text-sm font-bold transition-all ${currentView === 'my_dashboard' ? 'bg-orange-600 text-white shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <LayoutDashboard size={16} /> Meu Painel
                                </button>
                            </div>
                        </div>

                        {currentView === 'my_dashboard' ? (
                            <TeacherDashboard
                                reports={reports}
                                professorName={currentUser.name}
                                dbAlunos={dbAlunos}
                                turmas={classes}
                                trimestres={trimestres}
                            />
                        ) : (
                            <>
                                {/* 1. Seleção de Turma e Trimestre */}
                                <section className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-bold text-slate-700 mb-2">1. Selecione a Turma</label>
                                            <div className="relative">
                                                <select
                                                    value={selectedClass}
                                                    onChange={(e) => {
                                                        setSelectedClass(e.target.value);
                                                        setSelectedStudents([]);
                                                    }}
                                                    className="w-full p-3 pl-4 pr-10 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 outline-none appearance-none font-medium text-slate-700 cursor-pointer"
                                                >
                                                    <option value="" disabled>Escolha uma turma...</option>
                                                    {classes.map(t => <option key={t} value={t}>{t}</option>)}
                                                </select>
                                                <ChevronDown className="absolute right-3 top-3.5 text-slate-400 pointer-events-none" size={20} />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-bold text-slate-700 mb-2">2. Trimestre (Obrigatório)</label>
                                            <div className="relative">
                                                <select
                                                    value={selectedTrimestre}
                                                    onChange={(e) => setSelectedTrimestre(e.target.value)}
                                                    className="w-full p-3 pl-4 pr-10 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 outline-none appearance-none font-medium text-slate-700 cursor-pointer"
                                                >
                                                    <option value="" disabled>Selecione o período...</option>
                                                    {trimestres.map(t => <option key={t} value={t}>{t}</option>)}
                                                </select>
                                                <ChevronDown className="absolute right-3 top-3.5 text-slate-400 pointer-events-none" size={20} />
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                {selectedClass && (
                                    <>
                                        <section>
                                            <div className="flex justify-between items-end mb-3">
                                                <label className="text-sm font-bold text-slate-700">3. Selecione os Alunos ({selectedStudents.length})</label>
                                                {selectedStudents.length > 0 && (
                                                    <button onClick={() => setSelectedStudents([])} className="text-xs text-red-500 font-medium">Limpar</button>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                                                {filteredStudents.map(aluno => (
                                                    <div
                                                        key={aluno.id}
                                                        onClick={() => handleStudentToggle(aluno.id)}
                                                        className={`cursor-pointer rounded-xl border-2 transition-all duration-200 relative overflow-hidden group ${selectedStudents.includes(aluno.id) ? 'border-orange-500 bg-orange-50 shadow-md transform scale-[1.02]' : 'border-slate-200 bg-white hover:border-orange-200'}`}
                                                    >
                                                        {selectedStudents.includes(aluno.id) && (
                                                            <div className="absolute top-1 right-1 bg-orange-500 text-white rounded-full p-0.5 z-10"><Check size={12} strokeWidth={3} /></div>
                                                        )}
                                                        <div className="p-3 flex flex-col items-center text-center">
                                                            <img src={aluno.foto} alt={aluno.nome} className="w-12 h-12 rounded-full mb-2 bg-slate-100" />
                                                            <h3 className="font-semibold text-slate-800 text-xs leading-tight">{aluno.nome}</h3>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </section>

                                        <section className="grid md:grid-cols-2 gap-6">
                                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-red-100">
                                                <div className="flex items-center gap-2 mb-4 text-red-600"><ShieldAlert size={20} /><h2 className="font-bold">Ocorrências</h2></div>
                                                <div className="space-y-2">
                                                    {COMPORTAMENTOS_LISTA.ocorrencias.map(item => (
                                                        <label key={item} className="flex items-center p-3 rounded-lg hover:bg-red-50 cursor-pointer border border-transparent hover:border-red-100 transition-colors">
                                                            <input type="checkbox" checked={selectedOcorrencias.includes(item)} onChange={() => handleTagToggle(item, 'ocorrencia')} className="w-5 h-5 text-red-600 rounded focus:ring-red-500" />
                                                            <span className="ml-3 text-slate-700 text-sm">{item}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-green-100">
                                                <div className="flex items-center gap-2 mb-4 text-green-600"><Star size={20} /><h2 className="font-bold">Elogios</h2></div>
                                                <div className="space-y-2">
                                                    {COMPORTAMENTOS_LISTA.elogios.map(item => (
                                                        <label key={item} className="flex items-center p-3 rounded-lg hover:bg-green-50 cursor-pointer border border-transparent hover:border-green-100 transition-colors">
                                                            <input type="checkbox" checked={selectedElogios.includes(item)} onChange={() => handleTagToggle(item, 'elogio')} className="w-5 h-5 text-green-600 rounded focus:ring-green-500" />
                                                            <span className="ml-3 text-slate-700 text-sm">{item}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        </section>

                                        <section className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 space-y-4">
                                            <div>
                                                <label className="block text-sm font-bold text-slate-700 mb-1">Outros (Opcional)</label>
                                                <input type="text" value={otherReason} onChange={(e) => setOtherReason(e.target.value)} placeholder="Motivo específico..." className="w-full p-3 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-bold text-slate-700 mb-1 flex justify-between">
                                                    Descrição Detalhada
                                                    <button
                                                        onClick={toggleRecording}
                                                        className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full transition-all ${isRecording ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                                                    >
                                                        {isRecording ? <><MicOff size={12} /> Parar Gravação</> : <><Mic size={12} /> Gravar Áudio (Beta)</>}
                                                    </button>
                                                </label>
                                                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Descreva o contexto..." className="w-full p-3 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none resize-none"></textarea>
                                            </div>
                                        </section>

                                        <div className="sticky bottom-4 z-40 bg-white/90 backdrop-blur-md p-4 rounded-xl border border-orange-100 shadow-xl flex justify-between items-center">
                                            <span className="text-xs md:text-sm text-slate-500 font-medium">
                                                {selectedStudents.length} alunos • {selectedTrimestre || "Sem trimestre"}
                                            </span>
                                            <button
                                                onClick={handleSubmit}
                                                disabled={isSubmitting}
                                                className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-orange-200 transition-all flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                                            >
                                                {isSubmitting ? 'Salvando...' : <><Send size={18} /> Salvar Registro</>}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* --- VIEW: ADMINISTRAÇÃO --- */}
                {isAdmin && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-end border-b border-slate-200 pb-4 gap-4">
                            <div>
                                <h2 className="text-2xl font-bold text-slate-800">Painel Pedagógico</h2>
                                <p className="text-slate-500 text-sm">Monitoramento e Gestão</p>
                            </div>

                            <div className="flex bg-slate-100 p-1 rounded-lg">
                                <button
                                    onClick={() => setAdminTab('feed')}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-bold transition-all ${adminTab === 'feed' ? 'bg-white text-orange-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <List size={16} /> Feed
                                </button>
                                <button
                                    onClick={() => setAdminTab('dashboard')}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-bold transition-all ${adminTab === 'dashboard' ? 'bg-white text-orange-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <LayoutDashboard size={16} /> Dashboard
                                </button>
                            </div>
                        </div>

                        {adminTab === 'dashboard' ? (
                            <AdminDashboard
                                reports={reports}
                                dbAlunos={dbAlunos}
                                allStudentsFlat={allStudentsFlat}
                                turmas={classes}
                                trimestres={trimestres}
                            />

                        ) : (
                            <div className="grid gap-6">
                                <div className="flex justify-end mb-2">
                                    <button
                                        onClick={() => setShowArchived(!showArchived)}
                                        className={`text-xs font-bold flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all ${showArchived ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'}`}
                                    >
                                        {showArchived ? <><RefreshCcw size={12} /> Voltar ao Feed</> : <><Archive size={12} /> Ver Arquivados</>}
                                    </button>
                                </div>

                                {showArchived && (
                                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex flex-col md:flex-row gap-4 mb-4">
                                        <div className="flex-1 relative">
                                            <Search className="absolute left-3 top-3 text-slate-400" size={18} />
                                            <input
                                                type="text"
                                                placeholder="Buscar por nome do aluno..."
                                                value={archivedSearchTerm}
                                                onChange={(e) => setArchivedSearchTerm(e.target.value)}
                                                className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none text-sm"
                                            />
                                        </div>
                                        <div className="w-full md:w-48 relative">
                                            <select
                                                value={archivedFilterClass}
                                                onChange={(e) => setArchivedFilterClass(e.target.value)}
                                                className="w-full pl-3 pr-8 py-2 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none text-sm appearance-none cursor-pointer"
                                            >
                                                <option value="">Todas as Turmas</option>
                                                {classes.map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                            <ChevronDown className="absolute right-3 top-2.5 text-slate-400 pointer-events-none" size={16} />
                                        </div>
                                    </div>
                                )}

                                {reports.filter(r => {
                                    if (!showArchived) return !r.archived;
                                    // Logic for archived filters
                                    if (!r.archived) return false;
                                    const matchName = r.alunos[0].nome.toLowerCase().includes(archivedSearchTerm.toLowerCase());
                                    const matchClass = archivedFilterClass ? r.turma === archivedFilterClass : true;
                                    return matchName && matchClass;
                                }).length === 0 && (
                                        <div className="text-center py-20 text-slate-400">
                                            <FileText size={48} className="mx-auto mb-4 opacity-50" />
                                            <p>{showArchived ? "Nenhum item arquivado." : "Nenhum registro encontrado."}</p>
                                        </div>
                                    )}
                                {reports.filter(r => {
                                    if (!showArchived) return !r.archived;
                                    // Logic for archived filters
                                    if (!r.archived) return false;
                                    const matchName = r.alunos[0].nome.toLowerCase().includes(archivedSearchTerm.toLowerCase());
                                    const matchClass = archivedFilterClass ? r.turma === archivedFilterClass : true;
                                    return matchName && matchClass;
                                }).map((report) => (
                                    <div key={report.id} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                                        <div className="bg-slate-50 p-4 border-b border-slate-100 flex flex-wrap justify-between items-center gap-4">
                                            <div className="flex items-center gap-3">
                                                <div className="bg-orange-100 text-orange-800 p-2 rounded-lg font-bold text-xs uppercase tracking-wide">
                                                    {report.turma}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-slate-700 flex items-center gap-1">
                                                        <UserCircle size={14} className="text-slate-400" /> {report.professor}
                                                    </p>
                                                    <p className="text-xs text-slate-500">{report.data} • {report.trimestre}</p>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                {report.ocorrencias?.length > 0 && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded-md">Ocorrência</span>}
                                                {report.elogios?.length > 0 && <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-md">Elogio</span>}
                                            </div>
                                        </div>

                                        <div className="p-6 grid lg:grid-cols-2 gap-8">
                                            <div className="space-y-4">
                                                <div>
                                                    <div className="flex justify-between items-center mb-2">
                                                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Aluno</h4>
                                                        <span className="text-xs text-slate-400">E-mail: {report.alunos[0].emailResponsavel}</span>
                                                    </div>

                                                    <div
                                                        onClick={() => setStudentForModal({ nome: report.alunos[0].nome, turma: report.turma, foto: report.alunos[0].foto, emailResponsavel: report.alunos[0].emailResponsavel })}
                                                        className="flex items-center gap-2 bg-slate-50 border border-slate-100 pr-3 p-1 rounded-full cursor-pointer hover:bg-orange-50 hover:border-orange-200 transition-all group w-fit"
                                                    >
                                                        <img src={report.alunos[0].foto} alt="" className="w-8 h-8 rounded-full bg-white" />
                                                        <span className="text-sm font-bold text-slate-700 group-hover:text-orange-700">{report.alunos[0].nome}</span>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-4 text-sm">
                                                    {report.ocorrencias?.length > 0 && (
                                                        <div>
                                                            <h4 className="text-xs font-bold text-red-400 uppercase tracking-wider mb-1">Ocorrências</h4>
                                                            <ul className="list-disc list-inside text-slate-600">{report.ocorrencias.map(o => <li key={o}>{o}</li>)}</ul>
                                                        </div>
                                                    )}
                                                    {report.elogios?.length > 0 && (
                                                        <div>
                                                            <h4 className="text-xs font-bold text-green-400 uppercase tracking-wider mb-1">Elogios</h4>
                                                            <ul className="list-disc list-inside text-slate-600">{report.elogios.map(o => <li key={o}>{o}</li>)}</ul>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="relative group">
                                                <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl opacity-10 group-hover:opacity-20 transition duration-500"></div>
                                                <div className="relative bg-white h-full rounded-xl p-5 border border-orange-50 flex flex-col">
                                                    <div className="flex items-center gap-2 mb-3 text-orange-700">
                                                        <Sparkles size={18} />
                                                        <h3 className="font-bold text-sm">E-mail Gerado (IA)</h3>
                                                    </div>
                                                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 text-sm text-slate-700 leading-relaxed font-serif whitespace-pre-wrap flex-grow">
                                                        {report.textoGerado}
                                                    </div>
                                                    <div className="mt-4 flex justify-end gap-2">
                                                        <button
                                                            onClick={async () => {
                                                                if (!window.confirm(report.archived ? "Desarquivar este registro?" : "Arquivar este registro?")) return;
                                                                try {
                                                                    await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'reports', report.id), {
                                                                        archived: !report.archived
                                                                    });
                                                                } catch (err) { console.error(err); alert("Erro ao atualizar."); }
                                                            }}
                                                            className={`text-xs font-bold px-3 py-2 rounded hover:bg-slate-200 transition-colors flex items-center gap-1 ${report.archived ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-600'}`}
                                                        >
                                                            {report.archived ? <><RefreshCcw size={12} /> Restaurar</> : <><Archive size={12} /> Arquivar</>}
                                                        </button>
                                                        <button className="text-xs font-bold bg-slate-100 text-slate-600 px-3 py-2 rounded hover:bg-slate-200 transition-colors" onClick={() => copyToClipboard(report.textoGerado)}>Copiar</button>
                                                        <button
                                                            onClick={() => handleSendEmail(report)}
                                                            className="text-xs font-bold bg-orange-700 text-white px-3 py-2 rounded hover:bg-orange-800 shadow-sm transition-colors flex items-center gap-1"
                                                        >
                                                            <Send size={12} /> Enviar
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Modal para Admin */}
                        {studentForModal && (
                            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in">
                                <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
                                    <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50 sticky top-0 z-10">
                                        <h3 className="font-bold text-slate-800">Perfil Detalhado</h3>
                                        <button onClick={() => setStudentForModal(null)} className="p-1 hover:bg-slate-200 rounded-full transition-colors">
                                            <XIcon size={20} className="text-slate-500" />
                                        </button>
                                    </div>
                                    <div className="p-6">
                                        <StudentProfileStats
                                            reports={reports}
                                            student={studentForModal}
                                            studentName={studentForModal.nome}
                                            studentClass={studentForModal.turma}
                                            trimestres={trimestres}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
}

